import React, {useCallback, useContext, useEffect, useState} from "react";
import {useForm, FormProvider} from "react-hook-form";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faEyeSlash, faSearch} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";

// Contexts
import {NavContext} from "../../contexts/nav.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {getWithExpiry, setWithExpiry} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import InputCheckGroup from "../../components/form/InputCheckGroup.js";

// Style
import {
  HeadingCenter,
  Button,
  Form,
  FormField,
  Inline,
  Loader,
  Search,
  Label,
  SearchWrapper,
  SearchIcon
} from "../../style/components/general.js";

const CHECKSHEET_STATUSES = ["DUE", "INCOMPLETE", "OVERDUE"];

const ModalFacilityFilter = ({
  visible,
  setVisible,
  filters,
  setFilters,
  showArchived,
  setShowArchived,
  hasBackButton,
  goBack
}) => {
  const isMounted = useMountedState();

  const {allStates} = useContext(NavContext);

  const {api: apiFacilityType} = useApi("facility-types");
  const {api: apiRegion} = useApi("regions");
  const {api: apiFrequencies} = useApi("frequencies");

  const [loadingTypes, setLoadingTypes] = useState(false);
  const [facilityTypes, setFacilityTypes] = useState();
  const [regions, setRegions] = useState();
  const [loadingRegions, setLoadingRegions] = useState();
  const [loadingFrequencies, setLoadingFrequencies] = useState(false);
  const [availableFrequencies, setAvailableFrequencies] = useState();
  const [loading, setLoading] = useState(true);

  const form = useForm({
    defaultValues: {
      ...filters,
      archived: showArchived,
      checksheetSearch: filters?.checksheetSearch?.[0] || ""
    }
  });
  const {setValue, watch, handleSubmit, reset, register} = form;
  const watchArchived = watch("archived");

  const getTypes = useCallback(() => {
    setLoadingTypes(true);
    apiFacilityType
      .callGet()
      .then(({status, data}) => {
        if (status === 200 && data) setFacilityTypes(data);
      })
      .finally(() => setLoadingTypes(false));
  }, [apiFacilityType]);

  const getRegions = useCallback(() => {
    setLoadingRegions(true);
    apiRegion
      .callGet()
      .then(({status, data}) => {
        if (status === 200 && data) setRegions(data);
      })
      .finally(() => setLoadingRegions(false));
  }, [apiRegion]);

  const getFrequencies = useCallback(() => {
    setLoadingFrequencies(true);
    apiFrequencies
      .callGet()
      .then(({status, data}) => {
        if (status === 200 && data) setAvailableFrequencies(data);
      })
      .finally(() => setLoadingFrequencies(false));
  }, [apiFrequencies]);

  // Initial Load
  useEffect(() => {
    if (isMounted()) {
      getTypes();
      getRegions();
      getFrequencies();
    }
  }, [isMounted, getTypes, getRegions, getFrequencies]);

  useEffect(() => {
    if (
      facilityTypes &&
      !loadingTypes &&
      regions &&
      !loadingRegions &&
      availableFrequencies &&
      !loadingFrequencies &&
      allStates
    )
      setLoading(false);
  }, [
    allStates,
    facilityTypes,
    regions,
    loadingRegions,
    availableFrequencies,
    loadingFrequencies,
    loadingTypes
  ]);

  const applyFilters = ({
    types,
    regions: r,
    states,
    checksheets,
    checksheetSearch,
    frequencies,
    archived
  }) => {
    const updated = {};

    if (types?.length > 0) updated.types = types;
    if (r?.length > 0) updated.regions = r;
    if (states?.length > 0) updated.states = states;
    if (checksheets?.length > 0) updated.checksheets = checksheets;
    if (frequencies?.length > 0) updated.frequencies = frequencies;
    if (checksheetSearch?.length > 0) updated.checksheetSearch = [checksheetSearch];

    if (archived !== showArchived) {
      setWithExpiry("facilitiesDashboardFiltersArchived", archived);
      setShowArchived(archived);
    }

    setFilters(updated);
    setWithExpiry("facilitiesDashboardFilters", updated);
    setVisible(false);
  };

  const handleClear = () => {
    localStorage.removeItem("facilitiesDashboardFilters");
    localStorage.removeItem("facilitiesDashboardFiltersArchived");
    setFilters(undefined);
    setShowArchived(false);
    reset();
  };

  return (
    <Modal visible={visible} setVisible={setVisible} hasBackButton={hasBackButton} goBack={goBack}>
      <HeadingCenter>Facility Filters</HeadingCenter>
      <Form onSubmit={handleSubmit(applyFilters)}>
        <FormProvider {...form}>
          <FieldWrapper>
            {!loading ? (
              <>
                <FormField>
                  <InputCheckGroup
                    name="types"
                    label="Facility Type"
                    options={facilityTypes}
                    defaultSelection={getWithExpiry("facilitiesDashboardFilters")}
                  />
                </FormField>

                {!!regions?.length && (
                  <FormField>
                    <InputCheckGroup
                      name="regions"
                      label="Region"
                      options={regions.map(({id, name}) => ({
                        label: (name ?? "").toUpperCase(),
                        value: id
                      }))}
                      defaultSelection={getWithExpiry("facilitiesDashboardFilters")}
                    />
                  </FormField>
                )}

                {allStates && Object.keys(allStates)?.length > 0 && (
                  <FormField>
                    <InputCheckGroup
                      name="states"
                      label="Facility States"
                      options={Object.keys(allStates).map(key => ({
                        value: allStates[key].id,
                        label: key.toUpperCase()
                      }))}
                      defaultSelection={getWithExpiry("facilitiesDashboardFilters")}
                    />
                  </FormField>
                )}

                <FormField>
                  <InputCheckGroup
                    name="checksheets"
                    label="Checksheet Status"
                    options={CHECKSHEET_STATUSES}
                    defaultSelection={getWithExpiry("facilitiesDashboardFilters")}
                  />
                </FormField>

                <FormField>
                  <InputCheckGroup
                    name="frequencies"
                    label="Checksheet Frequency"
                    options={availableFrequencies.map(freq => ({
                      ...freq,
                      label: freq.label.toUpperCase()
                    }))}
                    defaultSelection={getWithExpiry("facilitiesDashboardFilters")}
                  />
                </FormField>

                <FormField>
                  <Label bold>Checksheet Name</Label>
                  <SearchWrapper>
                    <SearchIcon>
                      <FontAwesomeIcon icon={faSearch} />
                    </SearchIcon>
                    <Search
                      width={200}
                      type="text"
                      name="checksheetSearch"
                      {...register("checksheetSearch")}
                      onChange={e => setValue("checksheetSearch", e.target.value)}
                    />
                  </SearchWrapper>
                </FormField>
              </>
            ) : (
              <Loader />
            )}
          </FieldWrapper>

          <Inline>
            <Button type="submit">Apply</Button>
            <Button type="button" onClick={handleClear}>
              Clear
            </Button>
            <Button
              type="submit"
              title={watchArchived ? "Hide Archived" : "Show Archived"}
              onClick={() => setValue("archived", !watchArchived)}>
              <FontAwesomeIcon icon={watchArchived ? faEyeSlash : faEye} />
              &nbsp;Archived
            </Button>
          </Inline>
        </FormProvider>
      </Form>
    </Modal>
  );
};

ModalFacilityFilter.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  filters: PropTypes.objectOf(PropTypes.any),
  setFilters: PropTypes.func.isRequired,
  showArchived: PropTypes.bool,
  setShowArchived: PropTypes.func.isRequired,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func
};

// Style Overrides
const FieldWrapper = styled.div`
  min-height: 500px;
`;

export default ModalFacilityFilter;
